import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  message,
  InputNumber,
  Checkbox,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import MaturityScheduleService from '../../service/maturity-schedule-service/MaturitySchedule.service';
import EmployeeService from '../../service/employee-service/Employee.service';
import CreditCardService from '../../service/credit-card-service/Credit-card.service';
import { MaturityScheduleRequest } from '../../model/MaturitySchedule.model';
import { CreditCardResponse2 } from '../../model/Credit-card.model';
import EmployeeResponse from '../../model/Employee.model';
import useEmployee from '../../hook/useEmployee';
import { useIsAdmin } from '../../hook/useRole';

interface CreateMaturityScheduleModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  cardId: number;
}

const CreateMaturityScheduleModal: React.FC<
  CreateMaturityScheduleModalProps
> = ({ visible, onClose, onSuccess, cardId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [creditCard, setCreditCard] = useState<CreditCardResponse2 | null>(
    null
  );
  const [useDefaultFee, setUseDefaultFee] = useState(false);
  const { employee } = useEmployee();
  const isAdmin = useIsAdmin();

  // Load employees when modal opens
  useEffect(() => {
    if (visible) {
      loadEmployees();
      loadCreditCard();
    }
  }, [visible, cardId]);

  const loadCreditCard = async () => {
    try {
      const card = await CreditCardService.getCreditCardById(cardId);
      setCreditCard(card);

      // Customer 2026-05-29: khi khách hàng không có CTV (khách lẻ), mặc
      // định bật "Sử dụng phí mặc định" để feeApplied = defaultFeeRate của
      // thẻ. Người dùng vẫn có thể bỏ check để nhập số khác.
      const hasPartner = !!(card as any)?.customer?.partner;
      const hasDefaultFee =
        card &&
        (card as any).defaultFeeRate !== null &&
        (card as any).defaultFeeRate !== undefined;
      if (!hasPartner && hasDefaultFee) {
        setUseDefaultFee(true);
        form.setFieldsValue({ feeApplied: (card as any).defaultFeeRate });
      }
    } catch (error) {
      console.error('Error loading credit card:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const employeeList = await EmployeeService.getEmployeeList();
      setEmployees(employeeList);
    } catch (error) {
      message.error('Không thể tải danh sách nhân viên');
      console.error('Error loading employees:', error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (!employee?.id) {
        message.error('Không tìm thấy thông tin nhân viên');
        return;
      }

      setLoading(true);

      // Xử lý feeApplied để đảm bảo lấy đúng giá trị số
      let feeApplied = 0;
      if (
        useDefaultFee &&
        creditCard &&
        creditCard.defaultFeeRate !== null &&
        creditCard.defaultFeeRate !== undefined
      ) {
        feeApplied = creditCard.defaultFeeRate;
      } else if (typeof values.feeApplied === 'number') {
        feeApplied = values.feeApplied;
      } else if (values.feeApplied && !isNaN(Number(values.feeApplied))) {
        feeApplied = Number(values.feeApplied);
      }

      const request: MaturityScheduleRequest = {
        dueDate: values.dueDate.format('YYYY-MM-DD'),
        repaymentAmount: values.repaymentAmount,
        feeApplied: feeApplied,
        notes: values.notes || '',
        creditCardId: cardId,
        employeeIds: isAdmin ? values.employeeIds : [employee.id],
      };

      console.log('Creating maturity schedule with request:', request);
      console.log('Original values.feeApplied:', values.feeApplied);
      console.log('Processed feeApplied:', feeApplied);
      console.log('useDefaultFee:', useDefaultFee);

      await MaturityScheduleService.createMaturitySchedule(
        request,
        employee.id
      );

      message.success('Tạo kỳ đáo hạn thành công!');
      onSuccess();
      handleClose();
    } catch (error) {
      message.error('Có lỗi xảy ra khi tạo kỳ đáo hạn');
      console.error('Error creating maturity schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setUseDefaultFee(false);
    onClose();
  };

  // Handle checkbox change for using default fee
  const handleUseDefaultFeeChange = (checked: boolean) => {
    setUseDefaultFee(checked);
    if (
      checked &&
      creditCard &&
      creditCard.defaultFeeRate !== null &&
      creditCard.defaultFeeRate !== undefined
    ) {
      form.setFieldsValue({ feeApplied: creditCard.defaultFeeRate });
    } else {
      form.setFieldsValue({ feeApplied: 0 });
    }
  };

  return (
    <Modal
      title="Tạo kỳ đáo hạn mới"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={800}
      className="create-maturity-schedule-modal"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Due Date */}
          <Form.Item
            label="Ngày đáo hạn"
            name="dueDate"
            rules={[{ required: true, message: 'Vui lòng chọn ngày đáo hạn' }]}
          >
            <DatePicker
              placeholder="Chọn ngày đáo hạn"
              className="w-full"
              format="DD/MM/YYYY"
            />
          </Form.Item>

          {/* Repayment Amount */}
          <Form.Item
            label="Số tiền đáo hạn"
            name="repaymentAmount"
            rules={[
              { required: true, message: 'Vui lòng nhập số tiền đáo hạn' },
              { type: 'number', min: 1, message: 'Số tiền phải lớn hơn 0' },
            ]}
          >
            <InputNumber
              placeholder="Nhập số tiền"
              className="w-full"
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              min={0}
            />
          </Form.Item>

          {/* Fee Applied */}
          <Form.Item
            label={
              <span>
                Phí thu (%)
                {creditCard && (
                  <span className="text-gray-500 text-xs ml-1">
                    (Mặc định:{' '}
                    {creditCard.defaultFeeRate !== null
                      ? `${creditCard.defaultFeeRate}%`
                      : 'chưa có'}
                    )
                  </span>
                )}
              </span>
            }
            name="feeApplied"
          >
            <div className="space-y-2">
              <InputNumber
                placeholder="Nhập phí áp dụng"
                className="w-full"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                min={0}
                step={0.01}
                disabled={useDefaultFee}
              />
              {creditCard && creditCard.defaultFeeRate !== null && (
                <Checkbox
                  checked={useDefaultFee}
                  onChange={(e) => handleUseDefaultFeeChange(e.target.checked)}
                >
                  Sử dụng phí mặc định của thẻ
                </Checkbox>
              )}
            </div>
          </Form.Item>

          {/* Employee Multi Select */}
          {isAdmin ? (
            <Form.Item
              label="Nhân viên phụ trách"
              name="employeeIds"
              rules={[
                {
                  required: true,
                  message: 'Vui lòng chọn nhân viên phụ trách',
                },
              ]}
            >
              <Select
                mode="multiple"
                placeholder="Chọn nhân viên phụ trách"
                className="w-full"
                showSearch
                loading={loadingEmployees}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={employees.map((emp) => ({
                  value: emp.id,
                  label: emp.name,
                  key: emp.id,
                }))}
                maxTagCount="responsive"
              />
            </Form.Item>
          ) : (
            <div className="mb-2">
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Nhân viên phụ trách
              </label>
              <div className="text-sm text-gray-500 border rounded px-2 py-1 bg-gray-50">
                {employee?.name}
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <Form.Item label="Ghi chú" name="notes">
          <Input.TextArea
            placeholder="Nhập ghi chú"
            className="w-full"
            rows={3}
          />
        </Form.Item>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-2 pt-4">
          <Button onClick={handleClose}>Hủy</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<PlusOutlined />}
          >
            Tạo kỳ đáo hạn
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateMaturityScheduleModal;
